import { CUSTOM_TERMINALS } from '../constants/terminalConstants';
import { SEZIONI, SEZIONI_AWG } from '../constants/cableConstants';
import { TERMINAL_DATA } from '../constants/terminalConstants';
import { universalSort, createSortContext, getSortValue } from './sortingLogic';
import { COLOR_MAP, COLOR_TRANSLATION, COLORI_MAPPINGS } from '../constants/colorConstants';
import { getGroupTitle } from './groups';
import { translations } from '../i18n/translations';

const parseCache = new Map();
// canTerminalHandleMultipleSingles ora legge allowsMultiple da TERMINAL_DATA
const canTerminalHandleMultipleSingles = (sezione, terminal, terminali = TERMINAL_DATA) => {
  const config = (terminali || TERMINAL_DATA)[terminal] || {};
  return Boolean(terminal) && config.allowsMultiple === true;
};

export const parseSignature = (sig) => {
  if (parseCache.has(sig)) {
    return parseCache.get(sig);
  }

  const str = String(sig);

  // Gestione pattern con trattino - cattura TUTTO dopo il trattino
  const dashPattern = /^([A-Za-z]*\d*(?:\.\d+)?)-(.+)$/;
  const dashMatch = str.match(dashPattern);

  if (dashMatch) {
    const result = {
      letter: '',
      integer: 0,
      decimal: 0,
      original: sig,
      hasPattern: true,
      patternBase: dashMatch[1],
      patternSuffix: dashMatch[2]
    };

    parseCache.set(sig, result);
    return result;
  }

  // Logica esistente per pattern normali
  const letterMatch = str.match(/^([A-Za-z]*)/);
  const integerMatch = str.match(/([0-9]+)/);
  const decimalMatch = str.match(/\.([0-9]+)/);

  const result = {
    letter: letterMatch ? letterMatch[1] : '',
    integer: integerMatch ? parseInt(integerMatch[1]) : 0,
    decimal: decimalMatch ? parseInt(decimalMatch[1]) : 0,
    original: sig,
    hasPattern: false,
    patternBase: '',
    patternSuffix: ''
  };

  parseCache.set(sig, result);
  return result;
};

// Funzioni di conversione
export const convertMetricToAWG = (metricValue) => {
  if (metricValue == null) return metricValue;
  const numValue = parseFloat(metricValue);
  if (isNaN(numValue)) return metricValue;

  const exactSection = SEZIONI_AWG.find(s => s.metric === numValue);
  if (exactSection) return exactSection.val;

  // Se non c'è una corrispondenza esatta (es. per somma di cavi), trova l'AWG più vicino
  let closest = null;
  let minDiff = Infinity;
  for (let s of SEZIONI_AWG) {
    if (typeof s.metric === 'number') {
      let diff = Math.abs(s.metric - numValue);
      // A parità di distanza prendiamo la sezione maggiore (AWG minore)
      if (diff < minDiff || (diff === minDiff && closest && s.metric > closest.metric)) {
        minDiff = diff;
        closest = s;
      }
    }
  }
  return closest ? closest.val : metricValue;
};

export const convertAWGToMetric = (awgValue) => {
  const awgSection = SEZIONI_AWG.find(s => s.val === awgValue);
  return awgSection ? awgSection.metric : awgValue;
};

// Funzione per normalizzare i colori per la visualizzazione
export const normalizeColorForVisualization = (color) => {
  // Normalizza i colori per la visualizzazione (usa sempre le chiavi del COLOR_MAP)
  if (color === 'YE/GN') return 'GI/VE';
  if (COLOR_TRANSLATION[color]) {
    const translated = COLOR_TRANSLATION[color];
    // Controlla se il colore tradotto esiste nella mappa italiana
    return COLORI_MAPPINGS.it.includes(translated) ? translated : color;
  }
  return color;
};

export const getCableColorForSVG = (colorName) => {
  const normalized = normalizeColorForVisualization(colorName);
  return COLOR_MAP[normalized] || '#808080'; // Default grigio come nell'originale
};

export const formatSezione = (sezione, useAWG) => {
  return useAWG ? `${sezione} AWG` : `${sezione}mm²`;
};

/**
 * Determina il separatore corretto per la denominazione di un cavo multipolare.
 * - "G" se il cavo ha almeno un polo di terra (colore GI/VE o YE/GN)
 * - "x" minuscola se non ce ne sono
 *
 * Formato risultante: `${numeroPoli}G${sezione}` oppure `${numeroPoli}x${sezione}`
 *
 * @param {Array} poli - Array dei poli del cavo multipolare
 * @returns {string} Il separatore corretto: 'G' o 'x'
 */
export const getMultipolarSeparator = (poli) => {
  if (!Array.isArray(poli) || poli.length === 0) return 'x';
  const hasEarth = poli.some(p => p.colore === 'GI/VE' || p.colore === 'YE/GN');
  return hasEarth ? 'G' : 'x';
};

/**
 * Restituisce la label completa per un cavo multipolare nel formato NxS o NGS.
 * Esempio: "3G1.5" (3 poli con terra, sezione 1.5) o "4x1.5" (4 poli senza terra)
 *
 * @param {Array} poli - Array dei poli del cavo multipolare
 * @param {boolean} useAWG - Se true, usa la sezione in formato AWG
 * @returns {string} La label nel formato corretto
 */
export const getMultipolarLabel = (poli, useAWG = false) => {
  if (!Array.isArray(poli) || poli.length === 0) return '';
  const count = poli.length;
  const section = poli[0]?.sezione || (useAWG ? 20 : 0.5);
  const separator = getMultipolarSeparator(poli);
  return `${count}${separator}${section}`;
};

export const getSezioniArray = (useAWG) => {
  return useAWG ? SEZIONI_AWG : SEZIONI;
};

// Rimuove i punti virtuali (·) usati per raggruppare cavi in multipolari senza mostrare nello schematico
export const stripVirtualDots = (text) => {
  if (!text) return text;
  // Rimuove tutti i caratteri · (middle dot) dal testo
  return String(text).replace(/·/g, '').trim();
};

export const preprocessTextForWrapping = (text) => {
  if (!text) return text;
  // Prima rimuovi i punti virtuali per la visualizzazione schematica
  let processedText = stripVirtualDots(String(text).trim());

  const separatorPatterns = [
    { regex: /^(.+?)\s+(▲|▼)$/, replacement: '$1 ($2)' },
    { regex: /^(.+?)-(.+)$/, replacement: '$1 ($2)' },
    { regex: /^(.+?)_(.+)$/, replacement: '$1 ($2)' },
  ];

  for (const pattern of separatorPatterns) {
    if (pattern.regex.test(processedText)) {
      processedText = processedText.replace(pattern.regex, pattern.replacement);
      break;
    }
  }
  return processedText.replace(/\s+/g, ' ').trim();
};

// ==================== FUNZIONI HELPER PER TERMINALI ====================

export const isSingleTerminal = (terminal, terminali = TERMINAL_DATA) => {
  if (!terminal) return false;
  const config = (terminali || TERMINAL_DATA)[terminal] || {};
  return terminal.startsWith('PS') && !config.allowsMultiple;
};

export const isDoubleTerminal = (terminal, terminali = TERMINAL_DATA) => {
  if (!terminal) return false;
  const config = (terminali || TERMINAL_DATA)[terminal] || {};
  return terminal.startsWith('PD') || config.allowsMultiple === true;
};

export const areTerminalsCompatible = (terminalA, terminalB) => {
  if (terminalA !== terminalB) return false;

  // I terminali SPx non sono mai compatibili tra loro
  const bothAreSpellatura = (
    terminalA && terminalA.startsWith('SP') && terminalA !== 'SP' &&
    terminalB && terminalB.startsWith('SP') && terminalB !== 'SP'
  );

  return !bothAreSpellatura;
};

// ==================== FUNZIONI DISPLAY NAME ====================

export const getEarthCableDisplayName = (cable, language) => {
  // Se ha già una siglatura, usala
  if (cable.siglatura && String(cable.siglatura).trim() !== '') {
    return cable.siglatura;
  }

  // Se è un cavo di terra senza siglatura
  const isEarthWire = cable.colore === 'GI/VE' || cable.colore === 'YE/GN';
  if (isEarthWire) {
    return language === 'en' ? 'GND' : 'TERRA';
  }

  // Altrimenti sono senza siglatura
  return language === 'en' ? '[No marking]' : '[Senza siglatura]';
};

export const getCableDisplayName = (cable, language, settings) => {
  if (cable.poli && Array.isArray(cable.poli)) {
    if (cable.nomeCavo && cable.nomeCavo.trim() !== '') {
      return cable.nomeCavo;
    } else {
      return language === 'en' ? `Multipolar Cable` : `Cavo Multipolare`;
    }
  }

  if (settings.marcaturePerCollegamento) {
    // Modalità collegamento - mostra i collegamenti
    const connA = cable.collegamentoA || '';
    const connB = cable.collegamentoB || '';

    if (connA && connB) {
      return `${connA} → ${connB}`;
    } else if (connA) {
      return `${connA} → —`;
    } else if (connB) {
      return `— → ${connB}`;
    } else {
      return language === 'en' ? '[No connections]' : '[Senza collegamenti]';
    }
  } else {
    // Modalità normale - usa la logica esistente
    return getEarthCableDisplayName(cable, language);
  }
};

export const getDisplayTitle = (displayKey, sortedCables, isGrouppoForzato = false, nomeGruppo = null, settings = {}) => {
  const language = settings?.lingua || 'it';
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let text = translations[language];
    for (const k of keys) {
      if (text && typeof text === 'object' && k in text) {
        text = text[k];
      } else {
        return key;
      }
    }
    if (typeof text === 'string' && params) {
      return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
    }
    return text || key;
  };

  if (isGrouppoForzato && nomeGruppo) {
    const customTitle = getGroupTitle(nomeGruppo);
    if (customTitle && customTitle.trim() !== '') {
      return customTitle;
    }

    // Se tutti i cavi hanno la stessa siglatura, usa la logica normale
    const siglature = [...new Set(sortedCables.map(cable =>
      settings.marcaturePerCollegamento
        ? getCableDisplayName(cable, settings.lingua, settings)
        : cable.siglatura
    ).filter(sig => sig != null && String(sig).trim() !== ''))];

    if (siglature.length === 1) {
      // Tutti hanno la stessa siglatura, usa la logica normale
      if (!settings.marcaturePerCollegamento) {
        return `${t('cableList.cableLabel')} ${siglature[0]}`;
      } else {
        if (sortedCables.length === 1) {
          return `${t('cableList.cableLabel')} #${sortedCables[0].originalIndex + 1}`;
        } else {
          const numbers = sortedCables.map(c => `#${c.originalIndex + 1}`).join(', ');
          return `${t('cableList.cableLabel')} ${numbers}`;
        }
      }
    }

    return `${nomeGruppo}`;
  }

  const firstCable = sortedCables[0];
  if (firstCable.poli && Array.isArray(firstCable.poli)) {
    // Per cavi multipolari, usa il nomeCavo
    const displayName = firstCable.nomeCavo && firstCable.nomeCavo.trim() !== ''
      ? firstCable.nomeCavo
      : language === 'en' ? `Multipolar Cable #${firstCable.originalIndex + 1}` : `Cavo Multipolare #${firstCable.originalIndex + 1}`;
    return `${t('cableList.cableLabel')} ${displayName}`;
  }

  // Logica originale per casi non-gruppo
  if (!settings.marcaturePerCollegamento) {
    // Modalità normale - usa siglatura
    const siglatura = sortedCables[0].siglatura;
    const siglaturaStr = String(siglatura || '');
    return `${t('cableList.cableLabel')} ${displayKey.includes('_') ? siglaturaStr.split('_')[0] : siglaturaStr}`;
  } else {
    // Modalità collegamento - usa numerazione
    if (sortedCables.length === 1) {
      return `${t('cableList.cableLabel')} #${sortedCables[0].originalIndex + 1}`;
    } else {
      const numbers = sortedCables.map(c => `#${c.originalIndex + 1}`).join(', ');
      return `${t('cableList.cableLabel')} ${numbers}`;
    }
  }
};

// ==================== FUNZIONI DIMENSIONI CAVI ====================

export const getEffectiveSectionForDisplay = (sectionValue, useAWG = false) => {
  if (useAWG) {
    return SEZIONI_AWG.find(s => s.val === sectionValue)?.metric || sectionValue;
  }
  return SEZIONI.find(s => s.val === sectionValue)?.metric || sectionValue;
};

export const getCableWidthForTessera = (sectionValue, useAWG = false) => {
  const effectiveSection = getEffectiveSectionForDisplay(sectionValue, useAWG);
  return `${2 + effectiveSection * 3}px`;
};

export const getCableHeightForPreview = (sectionValue, useAWG = false, maxHeight = 24, availableSections = SEZIONI) => {
  const effectiveSection = getEffectiveSectionForDisplay(sectionValue, useAWG);

  const maxSection = Math.max(...availableSections.map(s =>
    getEffectiveSectionForDisplay(s.val, useAWG)
  ));

  const minHeight = 4;
  const height = Math.max(minHeight, (effectiveSection / maxSection) * maxHeight);
  return `${height}px`;
};

export const getStrokeWidth = (sectionValue, isHovered = false, useAWG = false) => {
  const effectiveSection = getEffectiveSectionForDisplay(sectionValue, useAWG);
  const base = 2 + 1.5 * (parseFloat(effectiveSection) || 0.5);
  return isHovered ? base + 1 : base;
};

// ==================== FUNZIONE PRINCIPALE DI ANALISI ====================

export const analyzeAndGroupCables = (cablesArr, settings) => {
  const subGroups = [];
  const processed = new Set();

  const areTerminalsCompatible = (terminalA, terminalB) => {
    if (terminalA !== terminalB) return false;

    // I terminali SPx non sono mai compatibili tra loro
    const bothAreSpellatura = (
      terminalA && terminalA.startsWith('SP') && terminalA !== 'SP' &&
      terminalB && terminalB.startsWith('SP') && terminalB !== 'SP'
    );

    return !bothAreSpellatura;
  };

  cablesArr.forEach((cable, idx) => {
    if (processed.has(idx)) return;

    const hasDoubleTerminalA = isDoubleTerminal(cable.terminaleA);
    const hasDoubleTerminalB = isDoubleTerminal(cable.terminaleB);
    const hasSingleTerminalA = isSingleTerminal(cable.terminaleA);
    const hasSingleTerminalB = isSingleTerminal(cable.terminaleB);

    const shouldBeIsolated = !cablesArr.some((other, otherIdx) => {
      if (otherIdx === idx) return false;

      const shareCollegamentoA = cable.collegamentoA &&
        (cable.collegamentoA === other.collegamentoA || cable.collegamentoA === other.collegamentoB);
      const shareCollegamentoB = cable.collegamentoB &&
        (cable.collegamentoB === other.collegamentoA || cable.collegamentoB === other.collegamentoB);

      let hasCompatibleTerminals = false;

      if (shareCollegamentoA) {
        if (cable.collegamentoA === other.collegamentoA) {
          hasCompatibleTerminals = areTerminalsCompatible(cable.terminaleA, other.terminaleA);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && cable.terminaleA === other.terminaleA;
          }
        } else if (cable.collegamentoA === other.collegamentoB) {
          hasCompatibleTerminals = areTerminalsCompatible(cable.terminaleA, other.terminaleB);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && cable.terminaleA === other.terminaleB;
          }
        }
      }

      if (shareCollegamentoB && !hasCompatibleTerminals) {
        if (cable.collegamentoB === other.collegamentoA) {
          hasCompatibleTerminals = areTerminalsCompatible(cable.terminaleB, other.terminaleA);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && cable.terminaleB === other.terminaleA;
          }
        } else if (cable.collegamentoB === other.collegamentoB) {
          hasCompatibleTerminals = areTerminalsCompatible(cable.terminaleB, other.terminaleB);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && cable.terminaleB === other.terminaleB;
          }
        }
      }

      const canSingleTerminalHandleMultiple = (cable, terminalSide) => {
        const terminal = terminalSide === 'A' ? cable.terminaleA : cable.terminaleB;
        return canTerminalHandleMultipleSingles(cable.sezione, terminal);
      };

      if (shareCollegamentoA && hasSingleTerminalA && !hasCompatibleTerminals) {
        const canHandleMultiple = canSingleTerminalHandleMultiple(cable, 'A') ||
          canSingleTerminalHandleMultiple(other, 'A');
        if (!canHandleMultiple) {
          return false;
        }
      }
      if (shareCollegamentoB && hasSingleTerminalB && !hasCompatibleTerminals) {
        const canHandleMultiple = canSingleTerminalHandleMultiple(cable, 'B') ||
          canSingleTerminalHandleMultiple(other, 'B');
        if (!canHandleMultiple) {
          return false;
        }
      }

      return (shareCollegamentoA || shareCollegamentoB) && hasCompatibleTerminals;
    });

    if (shouldBeIsolated) {
      processed.add(idx);
      subGroups.push({
        cables: [cable],
        isSingle: true
      });
      return;
    }

    const group = [cable];
    const toProcess = [cable];
    processed.add(idx);
    const processedInGroup = new Set([idx]);

    while (toProcess.length > 0) {
      const currentCable = toProcess.shift();

      cablesArr.forEach((otherCable, otherIdx) => {
        if (processed.has(otherIdx) || processedInGroup.has(otherIdx)) return;

        let shouldConnect = false;
        let hasCompatibleTerminals = false;

        if (currentCable.collegamentoA && otherCable.collegamentoA === currentCable.collegamentoA) {
          hasCompatibleTerminals = areTerminalsCompatible(currentCable.terminaleA, otherCable.terminaleA);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && currentCable.terminaleA === otherCable.terminaleA;
          }
          shouldConnect = hasCompatibleTerminals && !(isSingleTerminal(currentCable.terminaleA) && isSingleTerminal(otherCable.terminaleA));
        } else if (currentCable.collegamentoA && otherCable.collegamentoB === currentCable.collegamentoA) {
          hasCompatibleTerminals = areTerminalsCompatible(currentCable.terminaleA, otherCable.terminaleB);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && currentCable.terminaleA === otherCable.terminaleB;
          }
          shouldConnect = hasCompatibleTerminals && !(isSingleTerminal(currentCable.terminaleA) && isSingleTerminal(otherCable.terminaleB));
        }

        if (!shouldConnect && currentCable.collegamentoB && otherCable.collegamentoA === currentCable.collegamentoB) {
          hasCompatibleTerminals = areTerminalsCompatible(currentCable.terminaleB, otherCable.terminaleA);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && currentCable.terminaleB === otherCable.terminaleA;
          }
          shouldConnect = hasCompatibleTerminals && !(isSingleTerminal(currentCable.terminaleB) && isSingleTerminal(otherCable.terminaleA));
        } else if (!shouldConnect && currentCable.collegamentoB && otherCable.collegamentoB === currentCable.collegamentoB) {
          hasCompatibleTerminals = areTerminalsCompatible(currentCable.terminaleB, otherCable.terminaleB);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && currentCable.terminaleB === otherCable.terminaleB;
          }
          shouldConnect = hasCompatibleTerminals && !(isSingleTerminal(currentCable.terminaleB) && isSingleTerminal(otherCable.terminaleB));
        }

        if (shouldConnect) {
          // Controllo generale di consecutività - i cavi devono essere consecutivi per essere raggruppati
          const indexDifference = Math.abs(currentCable.originalIndex - otherCable.originalIndex);
          if (indexDifference !== 1) {
            shouldConnect = false;
          }

          if (shouldConnect) {
            // In modalità collegamento, la siglatura non è un fattore di raggruppamento
            const hasDifferentSignature = settings.marcaturePerCollegamento
              ? false
              : (currentCable.siglatura !== otherCable.siglatura);

            const isSingleButCanHandleMultiple = (cable, terminal) => {
              return canTerminalHandleMultipleSingles(cable.sezione, terminal);
            };

            const otherHasSingleOnSharedConnection =
              (otherCable.collegamentoA === currentCable.collegamentoA && isSingleTerminal(otherCable.terminaleA) && !isSingleButCanHandleMultiple(otherCable, otherCable.terminaleA)) ||
              (otherCable.collegamentoA === currentCable.collegamentoB && isSingleTerminal(otherCable.terminaleA) && !isSingleButCanHandleMultiple(otherCable, otherCable.terminaleA)) ||
              (otherCable.collegamentoB === currentCable.collegamentoA && isSingleTerminal(otherCable.terminaleB) && !isSingleButCanHandleMultiple(otherCable, otherCable.terminaleB)) ||
              (otherCable.collegamentoB === currentCable.collegamentoB && isSingleTerminal(otherCable.terminaleB) && !isSingleButCanHandleMultiple(otherCable, otherCable.terminaleB));

            if (!otherHasSingleOnSharedConnection && !hasDifferentSignature) {
              group.push(otherCable);
              toProcess.push(otherCable);
              processed.add(otherIdx);
              processedInGroup.add(otherIdx);
            }
          }
        }
      });
    }

    if (group.length === 1) {
      subGroups.push({
        cables: group,
        isSingle: true
      });
    } else {
      const finalGroups = splitGroupByTerminalTypes(group);
      finalGroups.forEach((finalGroup) => {
        subGroups.push({
          cables: finalGroup,
          isSingle: finalGroup.length === 1
        });
      });
    }
  });

  subGroups.forEach(group => {
    group.cables.forEach(cable => {
      cable.type = group.isSingle ? 'single' : 'multiple';
      cable.groupSize = group.cables.length;
    });
  });

  return subGroups;
};

// Funzione helper per dividere gruppi basandosi sui tipi di terminali
const splitGroupByTerminalTypes = (group) => {
  if (group.length <= 1) return [group];

  const connectionMap = {};

  group.forEach(cable => {
    if (cable.collegamentoA) {
      if (!connectionMap[cable.collegamentoA]) {
        connectionMap[cable.collegamentoA] = [];
      }
      connectionMap[cable.collegamentoA].push({
        cable,
        terminal: cable.terminaleA,
        side: 'A'
      });
    }
    if (cable.collegamentoB) {
      if (!connectionMap[cable.collegamentoB]) {
        connectionMap[cable.collegamentoB] = [];
      }
      connectionMap[cable.collegamentoB].push({
        cable,
        terminal: cable.terminaleB,
        side: 'B'
      });
    }
  });

  const singleOnlyConnections = new Set();
  Object.entries(connectionMap).forEach(([conn, items]) => {
    if (items.length > 1 && items.every(item => {
      // MODIFICA: Solo se è terminale singolo E non può gestire multipli
      return isSingleTerminal(item.terminal) &&
        !canTerminalHandleMultipleSingles(item.cable.sezione, item.terminal);
    })) {
      singleOnlyConnections.add(conn);
    }
  });

  if (singleOnlyConnections.size === 0) {
    return [group];
  }

  const separatedCables = [];
  const remainingCables = [];

  group.forEach(cable => {
    const shouldSeparate =
      (singleOnlyConnections.has(cable.collegamentoA) && isSingleTerminal(cable.terminaleA)) ||
      (singleOnlyConnections.has(cable.collegamentoB) && isSingleTerminal(cable.terminaleB));

    if (shouldSeparate) {
      separatedCables.push(cable);
    } else {
      remainingCables.push(cable);
    }
  });

  const result = [];
  separatedCables.forEach(cable => {
    result.push([cable]);
  });

  if (remainingCables.length > 0) {
    result.push(remainingCables);
  }

  return result;
};

export const getGroupedCablesAsArray = (groupedCablesObject, cables, listSortMode, settings) => {
  if (!groupedCablesObject || Object.keys(groupedCablesObject).length === 0) {
    return [];
  }

  const groupsArray = Object.entries(groupedCablesObject).map(([key, value]) => {
    const cablesInGroup = Array.isArray(value) ? value : value.allCables;
    return {
      key: key,
      groupData: value,
      cables: cablesInGroup,
      isForcedGroup: !Array.isArray(value),
      firstCable: cablesInGroup[0],
    };
  });

  return groupsArray.sort((a, b) => {
    const getFirstIndex = (groupData) => {
      if (Array.isArray(groupData.cables)) {
        // Gruppo normale - array di cavi
        return Math.min(...groupData.cables.map(c => c.originalIndex));
      } else if (groupData.groupData && groupData.groupData.type === 'forced_group') {
        // Gruppo forzato - usa allCables
        return Math.min(...groupData.groupData.allCables.map(c => c.originalIndex));
      } else {
        // Fallback
        return 0;
      }
    };

    const firstIndexA = getFirstIndex(a);
    const firstIndexB = getFirstIndex(b);

    if (listSortMode === 'numerazione') {
      return firstIndexA - firstIndexB;
    }

    // Per altri ordinamenti, usa il sistema unificato
    const context = createSortContext(settings.lingua);

    const getCablesArray = (groupData) => {
      if (Array.isArray(groupData.cables)) {
        return groupData.cables;
      } else if (groupData.groupData && groupData.groupData.type === 'forced_group') {
        return groupData.groupData.allCables;
      } else {
        return [];
      }
    };

    const groupA = { cables: getCablesArray(a) };
    const groupB = { cables: getCablesArray(b) };

    const [sortedA, sortedB] = universalSort([groupA, groupB], listSortMode, context);
    return sortedA === groupA ? -1 : 1;
  });
};

// Ottiene le siglature da stampare per un cavo in modalità collegamento
export const getSiglaturaPerStampa = (cable, settings) => {
  if (!settings.marcaturePerCollegamento) {
    // Modalità normale - usa siglatura
    return cable.siglatura;
  }

  // Modalità collegamento - estrai da entrambi i collegamenti
  const sigA = extractSiglaturaFromCollegamento(cable.collegamentoA);
  const sigB = extractSiglaturaFromCollegamento(cable.collegamentoB);

  // Ritorna array delle siglature uniche non vuote
  const siglature = [];
  if (sigA && sigA.trim() !== '') siglature.push(sigA);
  if (sigB && sigB.trim() !== '' && sigB !== sigA) siglature.push(sigB);

  return siglature;
};

export const extractSiglaturaFromCollegamento = (collegamento) => {
  if (!collegamento) return '';
  const match = collegamento.match(/^([^(]+)/);
  return match ? match[1].trim() : collegamento;
};

export const estimateTextWidth = (text, fontSize = 14, charWidth = 7) => {
  return text.length * charWidth;
};

export const replaceArrowsForPDF = (text) => {
  if (!text) return text;
  return String(text)
    .replace(/▲/g, 'UP')
    .replace(/▼/g, 'DW');
};

// Verifica se terminale ha grafica custom
export const hasCustomTerminalGraphic = (terminalCode) => {
  return CUSTOM_TERMINALS.includes(terminalCode);
};

export const getTerminalData = (terminalCode, settings = {}, terminali = null, isMultiple = false) => {
  if (!terminalCode) return { len: 0, peel: 0, nome: '' };

  const language = settings?.lingua || 'it';
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let text = translations[language];
    for (const k of keys) {
      if (text && typeof text === 'object' && k in text) {
        text = text[k];
      } else {
        return key;
      }
    }
    if (typeof text === 'string' && params) {
      return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
    }
    return text || key;
  };

  // Nuovo formato SPx/y dove x=len e y=peel
  if (terminalCode.startsWith('SP') && terminalCode.length > 2) {
    const match = terminalCode.match(/^SP(\d+)\/(\d+)$/);
    if (match) {
      const length = parseInt(match[1]) || 0;
      const peel = parseInt(match[2]) || 0;
      const printOffset = length;
      return {
        len: length,
        peel: peel,
        printOffset: printOffset,
        nome: t('terminals.customStripping', { length, peel })
      };
    }

    // Fallback per formato vecchio SPx (compatibilità)
    const length = parseInt(terminalCode.substring(2)) || 0;
    return {
      len: length,
      peel: 3,
      printOffset: length,
      nome: t('terminals.customStrippingOld', { length })
    };
  }

  // Usa terminali personalizzati se disponibili, altrimenti usa i valori hardcoded
  const terminalData = terminali || TERMINAL_DATA;
  const rawData = terminalData[terminalCode] || { len: 0, peel: 0, nome: '' };
  
  const baseData = { ...rawData };
  if (isMultiple) {
    baseData.len = baseData.multiLen !== undefined ? baseData.multiLen : baseData.len;
    baseData.peel = baseData.multiPeel !== undefined ? baseData.multiPeel : baseData.peel;
    baseData.printOffset = baseData.multiPrintOffset !== undefined ? baseData.multiPrintOffset : baseData.printOffset;
  }

  // Traduci il nome del terminale
  const terminalKey = terminalCode.replace(/\./g, '_');
  const translatedName = t(`terminals.${terminalKey}`);

  return {
    ...baseData,
    nome: translatedName !== `terminals.${terminalKey}` ? translatedName : baseData.nome
  };
};

// Split testo per etichette multi-linea
export const splitText = (text, maxLen = 14) => {
  if (!text) return [''];

  const processedText = preprocessTextForWrapping(text); // Applica la formattazione prima dello split

  const idx = processedText.indexOf(' ');
  if (idx === -1) return [processedText];

  const first = processedText.slice(0, idx);
  const rest = processedText.slice(idx + 1);

  if (rest.length <= maxLen) {
    return [first, rest];
  }

  const words = rest.split(' ');
  const lines = [];
  let current = '';

  words.forEach(word => {
    if ((current + ' ' + word).trim().length > maxLen) {
      if (current) lines.push(current.trim());
      current = word;
    } else {
      current += ' ' + word;
    }
  });
  if (current) lines.push(current.trim());

  return [first, ...lines];
};

export const getTerminalGroups = (terminali, language = 'it') => {
  if (!terminali || typeof terminali !== 'object') {
    return {};
  }

  const t = (key) => {
    const keys = key.split('.');
    let text = translations[language];
    if (!text) {
      // Fallback a italiano se la lingua non esiste
      text = translations['it'];
    }
    for (const k of keys) {
      if (text && typeof text === 'object' && k in text) {
        text = text[k];
      } else {
        // Se non trovato, prova con italiano come fallback
        if (language !== 'it') {
          text = translations['it'];
          for (const k2 of keys) {
            if (text && typeof text === 'object' && k2 in text) {
              text = text[k2];
            } else {
              return key;
            }
          }
          return text || key;
        }
        return key;
      }
    }
    return (typeof text === 'string' ? text : key);
  };

  // Costruisci la mappa inversa: TerminalCode -> GroupKey
  const terminalToGroupMap = {};

  // Importante: Importiamo TERMINAL_GROUPS dal constants
  const { TERMINAL_GROUPS } = require('../constants/terminalConstants');

  Object.entries(TERMINAL_GROUPS).forEach(([groupKey, codes]) => {
    codes.forEach(code => {
      terminalToGroupMap[code] = groupKey;
    });
  });

  // 2. Inizializza l'oggetto dei gruppi di output con le chiavi tradotte
  const groups = {};

  // Usa l'ordine delle chiavi definito in TERMINAL_GROUPS come ordine di visualizzazione
  const orderedKeys = Object.keys(TERMINAL_GROUPS);

  orderedKeys.forEach(groupKey => {
    // Traduci la chiave del gruppo
    const finalGroupName = t(groupKey);
    groups[finalGroupName] = [];
  });

  // 3. Itera sui terminali passati e assegnali ai gruppi
  Object.entries(terminali).forEach(([cod, term]) => {
    // Traduci sempre il nome del terminale usando il codice
    const terminalKey = cod.replace(/\./g, '_');
    const translationKey = `terminals.${terminalKey}`;
    const translatedName = t(translationKey);
    const finalName = (translatedName && translatedName !== translationKey) ? translatedName : (term.nome || cod);

    const termWithTranslatedName = {
      ...term,
      nome: finalName
    };

    // Determina il gruppo (chiave)
    // Se non trovato, usa 'terminals.others'
    const groupKey = terminalToGroupMap[cod] || 'terminals.others';

    // Traduci la chiave del gruppo per ottenere il nome visualizzato
    const targetGroupName = t(groupKey);

    // Se il gruppo non esiste (in caso di chiavi non presenti in TERMINAL_GROUPS iniziali), inizializzalo
    if (!groups[targetGroupName]) {
      groups[targetGroupName] = [];
    }

    groups[targetGroupName].push([cod, termWithTranslatedName]);
  });

  // 4. Rimuovi gruppi vuoti
  Object.keys(groups).forEach(key => {
    if (groups[key].length === 0) {
      delete groups[key];
    }
  });

  return groups;
};